'use client';

import { useCallback, useEffect, useState } from 'react';

import Image from 'next/image';

import { motion } from 'framer-motion';

import { ConfettiPieceType, RadialPieceType } from './interface';
import { core } from '../localization/core';

const cup = '/img/v2/home/l100.webp';
const roulette = '/img/v2/home/spins.webp';

const CONFETTI_TYPES = ['square', 'rectangle', 'circle'];
const CONFETTI_COLORS = [
  '#FFD700',
  '#F5DEB3',
  '#FFF8DC',
  '#D4AF37',
  '#E5C07B',
  '#C0B283',
  '#FAF3E0',
];

const ConfettiPiece = ({
  id, color, type, size, delay, xPosition, duration, rotation,
}: ConfettiPieceType) => {
  const getShape = () => {
    const baseShape = {
      width: size,
      height: size,
      borderRadius: '0%',
      id,
    };

    switch (type) {
      case 'square':
        return baseShape;
      case 'rectangle':
        return {
          ...baseShape,
          height: size * 0.5,
        };
      case 'circle':
        return {
          ...baseShape,
          borderRadius: '50%',
        };
      default:
        return baseShape;
    }
  };

  const shape = getShape();

  return (
    <motion.div
      className="absolute"
      style={{
        backgroundColor: color,
        width: shape.width,
        height: shape.height,
        borderRadius: shape.borderRadius,
        zIndex: 10,
        top: '-20px',
        left: `${xPosition}%`,
        opacity: 0,
      }}
      initial={{
        y: '-5vh',
        rotate: 0,
        opacity: 0,
      }}
      animate={{
        y: ['0vh', '120vh'],
        rotate: [0, rotation],
        opacity: [0, 1, 1, 0.8, 0],
      }}
      transition={{
        duration,
        delay,
        ease: 'easeIn',
        times: [0, 0.1, 0.8, 0.95, 1],
      }}
    />
  );
};

const RadialPiece = ({
  id, color, type, size, delay, angle, distance, duration, rotation,
}: RadialPieceType) => {
  const getShape = () => {
    const baseShape = {
      width: size,
      height: size,
      borderRadius: '0%',
      id,
    };

    switch (type) {
      case 'square':
        return baseShape;
      case 'rectangle':
        return {
          ...baseShape,
          height: size * 0.5,
        };
      case 'circle':
        return { width: size, height: size, borderRadius: '50%' };
      default:
        return baseShape;
    }
  };

  const shape = getShape();

  const finalX = Math.cos(angle) * distance;
  const finalY = Math.sin(angle) * distance;

  return (
    <motion.div
      className="absolute"
      style={{
        backgroundColor: color,
        width: shape.width,
        height: shape.height,
        borderRadius: shape.borderRadius,
        zIndex: 5,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        opacity: 0,
      }}
      initial={{
        x: 0,
        y: 0,
        rotate: 0,
        opacity: 0,
      }}
      animate={{
        x: [0, finalX],
        y: [0, finalY],
        rotate: [0, rotation],
        opacity: [0, 1, 0.8, 0],
      }}
      transition={{
        duration,
        delay,
        ease: 'easeOut',
        times: [0, 0.2, 0.8, 1],
      }}
    />
  );
};

const HeaderForm = ({
  view,
}: {
  view: string
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [confettiPieces, setConfettiPieces] = useState<ConfettiPieceType[]>([]);
  const [radialPieces, setRadialPieces] = useState<RadialPieceType[]>([]);
  const [confettiCount, setConfettiCount] = useState(0);
  const [uniqueIdCounter, setUniqueIdCounter] = useState(0);
  const maxPieces = 300;

  const generateConfettiPiece = useCallback(() => {
    const isLateral = Math.random() < 0.8;
    let xPosition;
    if (isLateral) {
      xPosition = Math.random() < 0.5 ? Math.random() * 20 : 80 + Math.random() * 20;
    } else {
      xPosition = 20 + Math.random() * 60;
    }

    const uniqueId = `${Date.now()}-${uniqueIdCounter}-${Math.random()}`;
    setUniqueIdCounter((prev) => { return prev + 1; });

    return {
      id: uniqueId,
      color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
      type: CONFETTI_TYPES[Math.floor(Math.random() * CONFETTI_TYPES.length)],
      size: Math.random() * 6 + 5,
      delay: Math.random() * 3,
      xPosition,
      duration: Math.random() * 2 + 2,
      rotation: (Math.random() - 0.5) * 720,
    };
  }, [uniqueIdCounter]);

  const generateRadialPieces = (count: number) => {
    const pieces: RadialPieceType[] = [];
    const baseTimestamp = Date.now();

    for (let i = 0; i < count; i++) {
      const angle = Math.random() * Math.PI * 4;
      const distance = Math.random() * 400 + 50;

      pieces.push({
        id: `radial-${baseTimestamp}-${i}-${Math.random()}`,
        color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        type: CONFETTI_TYPES[Math.floor(Math.random() * CONFETTI_TYPES.length)],
        size: Math.random() * 8 + 4,
        delay: Math.random() * 0.2,
        angle,
        distance,
        duration: Math.random() * 1 + 0.8,
        rotation: (Math.random() - 0.5) * 720,
      });
    }

    return pieces;
  };

  useEffect(() => {
    if (!isPlaying) return;

    const interval = setInterval(() => {
      if (confettiCount >= maxPieces) {
        clearInterval(interval);
        setIsPlaying(false);
        return;
      }

      const newPieces: ConfettiPieceType[] = [];
      const piecesToAdd = Math.floor(Math.random() * 200) + 1;

      for (let i = 0; i < piecesToAdd; i++) {
        newPieces.push(generateConfettiPiece());
      }

      setConfettiPieces((prev) => {
        return [...prev, ...newPieces];
      });
      setConfettiCount((prev) => {
        return prev + piecesToAdd;
      });

      if (confettiPieces.length > 100) {
        setConfettiPieces((prev) => {
          return prev.slice(prev.length - 100);
        });
      }
    }, 100);

    // eslint-disable-next-line consistent-return
    return () => {
      clearInterval(interval);
    };
  }, [isPlaying, confettiCount, confettiPieces.length, generateConfettiPiece]);

  useEffect(() => {
    if (view) {
      setIsPlaying(true);
      setConfettiCount(0);
      setConfettiPieces([]);

      setRadialPieces(generateRadialPieces(100));

      const timeout = setTimeout(() => {
        setIsPlaying(false);
        setConfettiPieces([]);
        setRadialPieces([]);
      }, 5000);

      return () => {
        clearTimeout(timeout);
      };
    }
    return undefined;
  }, [view]);
  return (
    <section className="relative flex flex-col items-center mt-0 w-full min-h-[200px]">
      <div className="pointer-events-none overflow-hidden absolute inset-0 -z-10">
        {radialPieces.map((piece) => {
          return (
            <RadialPiece
              key={piece.id}
              id={piece.id}
              color={piece.color}
              type={piece.type}
              size={piece.size}
              delay={piece.delay}
              angle={piece.angle}
              distance={piece.distance}
              duration={piece.duration}
              rotation={piece.rotation}
            />
          );
        })}

        {confettiPieces.map((piece) => {
          return (
            <ConfettiPiece
              key={piece.id}
              id={piece.id}
              color={piece.color}
              type={piece.type}
              size={piece.size}
              delay={piece.delay}
              xPosition={piece.xPosition}
              duration={piece.duration}
              rotation={piece.rotation}
            />
          );
        })}
      </div>
      <Image src={view !== 'casino' ? cup : roulette} alt="Regalo" width={310} height={300} loading="lazy" quality={70} className="z-20 mx-auto mt-5 saturate-100" />
      <div className="flex items-center justify-center gap-2 -mb-5">
        <p className="text-white text-xl text-center font-bold leading-none">{core.gift.descriptionForm}</p>
        <p className="text-hprimary-500 text-xl text-center font-bold leading-none">{core.gift.descriptionForm2}</p>
      </div>
    </section>
  );
};

export default HeaderForm;
